import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

type NewsItem = {
  id: string;
  title: string;
  slug: string;
  published_at: string | null;
  views: number | null;
};

type RankedNewsItem = NewsItem & {
  total: number;
};

const publishedNowFilter = () => {
  const now = new Date().toISOString();
  return `published_at.is.null,published_at.lte.${now}`;
};

function getCurrentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

function formatDate(date?: string | null) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("pt-BR");
}

export default function MostReadCommented() {
  const navigate = useNavigate();

  const [mostRead, setMostRead] = useState<RankedNewsItem[]>([]);
  const [mostCommented, setMostCommented] = useState<RankedNewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function carregarDados() {
      setLoading(true);

      const { start, end } = getCurrentMonthRange();

      const { data: pageViews } = await supabase
        .from("page_views")
        .select("news_id")
        .not("news_id", "is", null)
        .gte("created_at", start)
        .lt("created_at", end);

      const viewsByNews = new Map<string, number>();

      (pageViews || []).forEach((view: any) => {
        if (!view.news_id) return;
        viewsByNews.set(view.news_id, (viewsByNews.get(view.news_id) || 0) + 1);
      });

      let mostReadList: RankedNewsItem[] = [];

      if (viewsByNews.size > 0) {
        const ids = Array.from(viewsByNews.keys());

        const { data: readNews } = await supabase
          .from("news")
          .select("id, title, slug, published_at, views")
          .in("id", ids)
          .eq("status", "publicado")
          .or(publishedNowFilter());

        mostReadList = (readNews || [])
          .map((item: any) => ({
            ...item,
            total: viewsByNews.get(item.id) || 0,
          }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 5);
      } else {
        const { data: fallbackReadNews } = await supabase
          .from("news")
          .select("id, title, slug, published_at, views")
          .eq("status", "publicado")
          .or(publishedNowFilter())
          .gte("published_at", start)
          .lt("published_at", end)
          .order("views", { ascending: false })
          .limit(5);

        mostReadList = (fallbackReadNews || []).map((item: any) => ({
          ...item,
          total: item.views || 0,
        }));
      }

      const { data: monthComments } = await supabase
        .from("comments")
        .select("news_id")
        .eq("status", "aprovado")
        .gte("created_at", start)
        .lt("created_at", end);

      const commentsByNews = new Map<string, number>();

      (monthComments || []).forEach((comment: any) => {
        if (!comment.news_id) return;
        commentsByNews.set(comment.news_id, (commentsByNews.get(comment.news_id) || 0) + 1);
      });

      let mostCommentedList: RankedNewsItem[] = [];

      if (commentsByNews.size > 0) {
        const ids = Array.from(commentsByNews.keys());

        const { data: commentedNews } = await supabase
          .from("news")
          .select("id, title, slug, published_at, views")
          .in("id", ids)
          .eq("status", "publicado")
          .or(publishedNowFilter());

        mostCommentedList = (commentedNews || [])
          .map((item: any) => ({
            ...item,
            total: commentsByNews.get(item.id) || 0,
          }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 5);
      }

      setMostRead(mostReadList);
      setMostCommented(mostCommentedList);
      setLoading(false);
    }

    carregarDados();
  }, []);

  if (loading) {
    return (
      <section className="w-full py-8 md:py-12 bg-alvinegro-light">
        <div className="w-full px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {[1, 2].map((column) => (
              <div key={column} className="space-y-4 animate-pulse">
                <div className="h-7 w-48 bg-alvinegro-border rounded-lg mb-6" />
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="h-16 bg-white rounded-xl" />
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full py-8 md:py-12 bg-alvinegro-light">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <i className="ri-fire-line text-red-600 text-xl" />
              <h2 className="font-serif font-bold text-xl md:text-2xl text-alvinegro-black">
                Mais Lidas do MÃªs
              </h2>
            </div>

            <div className="space-y-4">
              {mostRead.length === 0 ? (
                <div className="bg-white rounded-xl p-5 text-sm text-alvinegro-muted">
                  Nenhuma leitura registrada neste mÃªs.
                </div>
              ) : (
                mostRead.map((news, index) => (
                  <button
                    key={news.id}
                    type="button"
                    onClick={() => navigate(`/noticias/${news.slug}`)}
                    className="w-full flex items-start gap-4 p-4 bg-white rounded-xl cursor-pointer hover:shadow-md transition-shadow group text-left"
                  >
                    <span className="text-2xl font-bold text-alvinegro-light group-hover:text-alvinegro-black transition-colors">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <div className="flex-1">
                      <h3 className="font-semibold text-sm text-alvinegro-black leading-snug line-clamp-2">
                        {news.title}
                      </h3>

                      <div className="flex items-center gap-2 mt-1 text-xs text-alvinegro-muted">
                        <span>{formatDate(news.published_at)}</span>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-6">
              <i className="ri-chat-3-line text-alvinegro-black text-xl" />
              <h2 className="font-serif font-bold text-xl md:text-2xl text-alvinegro-black">
                Mais Comentadas do MÃªs
              </h2>
            </div>

            <div className="space-y-4">
              {mostCommented.length === 0 ? (
                <div className="bg-white rounded-xl p-5 text-sm text-alvinegro-muted">
                  Nenhum comentÃ¡rio aprovado neste mÃªs.
                </div>
              ) : (
                mostCommented.map((news, index) => (
                  <button
                    key={news.id}
                    type="button"
                    onClick={() => navigate(`/noticias/${news.slug}`)}
                    className="w-full flex items-start gap-4 p-4 bg-white rounded-xl cursor-pointer hover:shadow-md transition-shadow group text-left"
                  >
                    <span className="text-2xl font-bold text-alvinegro-light group-hover:text-alvinegro-black transition-colors">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <div className="flex-1">
                      <h3 className="font-semibold text-sm text-alvinegro-black leading-snug line-clamp-2">
                        {news.title}
                      </h3>

                      <div className="flex items-center gap-2 mt-1 text-xs text-alvinegro-muted">
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-1-line" />
                          {news.total}
                        </span>
                        <span>â€¢</span>
                        <span>{formatDate(news.published_at)}</span>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
